import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { environment } from '@environments/environment'
import { BehaviorSubject, Observable } from 'rxjs'
import { EmailComponentType } from '../interfaces/email-component.interface'
import {
  AddComponentRequest,
  EmailTemplate,
  EmailTemplateListResponse,
  PreviewResponse,
  RenderRequest,
  RenderResponse,
  ReorderComponentsRequest,
  UpdateComponentsRequest
} from '../interfaces/email-template.interface'
import { CreateThemeRequest, EmailTheme, EmailThemeListResponse, EmailThemeResponse, UpdateThemeRequest } from '../interfaces/email-theme.interface'
import { MjmlService } from './mjml.service'

@Injectable({
  providedIn: 'root'
})
export class EmailComposerService {
  private selectedTheme$ = new BehaviorSubject<EmailTheme | null>(null)
  private availableComponents$ = new BehaviorSubject<EmailComponentType[]>([])
  private readonly baseUrl = `${environment.v2.baseUrl}email-composer`
  private readonly actionsBaseUrl = `${environment.v2.baseUrl}email-composer/actions`

  constructor(private http: HttpClient, private mjmlService: MjmlService) {}

  // Template Management
  getTemplates(search?: string, is_active?: boolean, is_system_template?: boolean): Observable<EmailTemplateListResponse> {
    let params = new HttpParams()

    if (search) {
      params = params.set('search', search)
    }

    if (is_active !== undefined) {
      params = params.set('is_active', is_active ? '1' : '0')
    }

    if (is_system_template !== undefined) {
      params = params.set('is_system_template', is_system_template.toString())
    }

    return this.http.get<EmailTemplateListResponse>(`${this.baseUrl}/templates`, { params })
  }

  getTemplate(id: number): Observable<{ data: EmailTemplate; message: string }> {
    return this.http.get<{ data: EmailTemplate; message: string }>(`${this.baseUrl}/templates/${id}`)
  }

  createTemplate(template: Partial<EmailTemplate>): Observable<{ data: EmailTemplate; message: string }> {
    return this.http.post<{ data: EmailTemplate; message: string }>(`${this.baseUrl}/templates`, template)
  }

  updateTemplate(id: number, template: Partial<EmailTemplate>): Observable<{ data: EmailTemplate; message: string }> {
    return this.http.patch<{ data: EmailTemplate; message: string }>(`${this.baseUrl}/templates/${id}`, template)
  }

  deleteTemplate(id: number): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.baseUrl}/templates/${id}`)
  }

  duplicateTemplate(id: number, duplicateData: { name: string; description?: string; slug?: string }): Observable<{ data: EmailTemplate; message: string }> {
    return this.http.post<{ data: EmailTemplate; message: string }>(`${this.baseUrl}/templates/${id}/duplicate`, duplicateData)
  }

  // Component Management
  addComponentToTemplate(templateId: number, componentData: AddComponentRequest): Observable<{ data: any; message: string }> {
    return this.http.post<{ data: any; message: string }>(`${this.baseUrl}/templates/${templateId}/components`, componentData)
  }
  updateComponentsToTemplate(templateId: number, componentsData: UpdateComponentsRequest): Observable<{ data: any; message: string }> {
    return this.http.patch<{ data: any; message: string }>(`${this.baseUrl}/templates/${templateId}/components`, componentsData)
  }

  reorderTemplateComponents(templateId: number, reorderData: ReorderComponentsRequest): Observable<{ message: string }> {
    return this.http.patch<{ message: string }>(`${this.baseUrl}/templates/${templateId}/components/reorder`, reorderData)
  }

  removeComponentFromTemplate(templateId: number, componentId: number): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.baseUrl}/templates/${templateId}/components/${componentId}`)
  }

  // Component Management
  getAvailableComponents(search?: string, category?: string, is_active?: boolean, is_system_component?: boolean): Observable<{ data: EmailComponentType[]; message: string }> {
    let params = new HttpParams()

    if (search) {
      params = params.set('search', search)
    }

    if (category) {
      params = params.set('category', category)
    }

    if (is_active !== undefined) {
      params = params.set('is_active', is_active ? '1' : '0')
    }

    if (is_system_component !== undefined) {
      params = params.set('is_system_component', is_system_component.toString())
    }

    return this.http.get<{ data: EmailComponentType[]; message: string }>(`${this.baseUrl}/components`, { params })
  }

  getComponentType(id: number): Observable<{ data: EmailComponentType; message: string }> {
    return this.http.get<{ data: EmailComponentType; message: string }>(`${this.baseUrl}/components/${id}`)
  }

  createComponent(component: Partial<EmailComponentType>): Observable<{ data: EmailComponentType; message: string }> {
    return this.http.post<{ data: EmailComponentType; message: string }>(`${this.baseUrl}/components`, component)
  }

  updateComponent(id: number, component: Partial<EmailComponentType>): Observable<{ data: EmailComponentType; message: string }> {
    return this.http.patch<{ data: EmailComponentType; message: string }>(`${this.baseUrl}/components/${id}`, component)
  }

  deleteComponent(id: number): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.baseUrl}/components/${id}`)
  }

  // Preview & Rendering
  previewTemplate(templateId: number, themeId?: number): Observable<PreviewResponse> {
    const params = themeId ? new HttpParams().set('theme_id', themeId.toString()) : new HttpParams()
    return this.http.get<PreviewResponse>(`${this.baseUrl}/templates/${templateId}/preview`, { params })
  }

  renderTemplate(templateId: number, renderData: RenderRequest): Observable<RenderResponse> {
    return this.http.post<RenderResponse>(`${this.baseUrl}/templates/${templateId}/render`, renderData)
  }

  // Send test email
  sendTestEmail(templateId: number, payload: { theme_id?: number | null; email: string; subject: string }): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.baseUrl}/templates/${templateId}/send-test-email`, payload)
  }

  // Theme Management
  getThemes(search?: string, is_active?: boolean, is_system_theme?: boolean, is_default?: boolean): Observable<EmailThemeListResponse> {
    let params = new HttpParams()

    if (search) {
      params = params.set('search', search)
    }

    if (is_active !== undefined) {
      params = params.set('is_active', is_active.toString())
    }

    if (is_system_theme !== undefined) {
      params = params.set('is_system_theme', is_system_theme.toString())
    }

    if (is_default !== undefined) {
      params = params.set('is_default', is_default.toString())
    }

    return this.http.get<EmailThemeListResponse>(`${this.baseUrl}/themes`, { params })
  }

  getTheme(id: number): Observable<EmailThemeResponse> {
    return this.http.get<EmailThemeResponse>(`${this.baseUrl}/themes/${id}`)
  }

  createTheme(theme: CreateThemeRequest): Observable<EmailThemeResponse> {
    return this.http.post<EmailThemeResponse>(`${this.baseUrl}/themes`, theme)
  }

  updateTheme(id: number, theme: UpdateThemeRequest): Observable<EmailThemeResponse> {
    return this.http.patch<EmailThemeResponse>(`${this.baseUrl}/themes/${id}`, theme)
  }

  deleteTheme(id: number): Observable<{ success: boolean; message: string; data: any[] }> {
    return this.http.delete<{ success: boolean; message: string; data: any[] }>(`${this.baseUrl}/themes/${id}`)
  }

  setDefaultTheme(id: number): Observable<EmailThemeResponse> {
    return this.http.patch<EmailThemeResponse>(`${this.baseUrl}/themes/${id}`, { is_default: true })
  }

  // State Management
  setSelectedTheme(theme: EmailTheme | null): void {
    this.selectedTheme$.next(theme)
  }

  getSelectedTheme(): Observable<EmailTheme | null> {
    return this.selectedTheme$.asObservable()
  }

  setAvailableComponents(components: EmailComponentType[]): void {
    this.availableComponents$.next(components)
  }

  getAvailableComponents$(): Observable<EmailComponentType[]> {
    return this.availableComponents$.asObservable()
  }

  // Component Rendering with MJML API
  renderComponent(component: EmailComponentType, theme?: EmailTheme | null, additionalVariables?: Record<string, any>): Observable<string> {
    let mjmlContent = component.mjml_template || component.html_template || ''

    // If no MJML template, create a basic preview
    if (!mjmlContent) {
      mjmlContent = `
        <mj-section background-color="{{theme.background_color}}">
          <mj-column>
            <mj-text font-size="{{theme.font_size}}" color="{{theme.text_color}}">
              <h3>${component.name}</h3>
              <p>${component.description || 'No preview available'}</p>
            </mj-text>
          </mj-column>
        </mj-section>
      `
    }

    // Build variables object combining component variables with additional ones
    const variables: Record<string, any> = { ...additionalVariables }

    // Add component variables with sample/default values
    if (component.variables) {
      component.variables.forEach((variable) => {
        const sample = this.getSampleValueForVariable(variable)
        // Fill by name if not provided
        if (variables[variable.name] === undefined) {
          variables[variable.name] = sample
        }
        // Also fill by slug if not provided and slug differs
        if (variable.slug && variables[variable.slug] === undefined) {
          variables[variable.slug] = sample
        }
      })
    }

    // Use the MJML service to render
    return this.mjmlService.renderMjml(mjmlContent, theme, variables)
  }

  // Actions API
  listActions(params?: { search?: string; is_active?: boolean; is_system_template?: boolean }): Observable<{ data: any[]; message: string }> {
    let httpParams = new HttpParams()
    if (params?.search) httpParams = httpParams.set('search', params.search)
    if (params?.is_active !== undefined) httpParams = httpParams.set('is_active', String(params.is_active))
    if (params?.is_system_template !== undefined) httpParams = httpParams.set('is_system_template', String(params.is_system_template))
    return this.http.get<{ data: any[]; message: string }>(`${this.actionsBaseUrl}`, { params: httpParams })
  }

  getAction(actionId: number): Observable<{ data: any; message: string }> {
    return this.http.get<{ data: any; message: string }>(`${this.actionsBaseUrl}/${actionId}`)
  }

  createAction(payload: { action: string; email_template_id: number }): Observable<{ data: any; message: string }> {
    return this.http.post<{ data: any; message: string }>(`${this.actionsBaseUrl}`, payload)
  }

  updateAction(actionId: number, payload: { action: string; email_template_id: number }): Observable<{ data: any; message: string }> {
    return this.http.patch<{ data: any; message: string }>(`${this.actionsBaseUrl}/${actionId}`, payload)
  }

  deleteAction(actionId: number): Observable<{ data: any[]; message: string }> {
    return this.http.delete<{ data: any[]; message: string }>(`${this.actionsBaseUrl}/${actionId}`)
  }

  private getSampleValueForVariable(variable: any): any {
    // Use default value if available, otherwise generate sample based on type
    if (variable.default_value !== undefined && variable.default_value !== null && variable.default_value !== '') {
      return variable.default_value
    }

    switch (variable.type) {
      case 'text':
        return 'Sample Text'
      case 'url':
        return 'https://example.com'
      case 'color':
        return '#3b82f6'
      case 'number':
        return 42
      case 'boolean':
        return true
      default:
        return 'Sample Value'
    }
  }
}
